/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { RESULTS_PER_PAGE } from "app/app-constants";
import { PagedResults } from "app/models/paged-results";
import { Zaa_ZonesResponseDto } from "app/models/zoneassignedassets/Zaa_ZonesResponeDto";
import { ZoneAssignedAssetsService } from "app/services/zone-assigned-assets.service";
import { SimpleIndefiniteProgressDialogComponent } from "../../simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "../../simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { ZoneAssignedAssetsDialogComponent } from "../dialog/zone-assigned-assets-dialog.component";
import { Zaa_AssetDto } from "app/models/zoneassignedassets/Zaa_AssetDto";

/**
 * Page Component displaying the 'Zone Assigned Asset' information.
 * This contains a table view for the Zones and the # of Assets assigned
 * to them, and allows the User to filter based on the Zone Name and select / 
 * edit the Assets assigned to the Zones. 
 */
@Component({
    templateUrl: './zone-assigned-assets.component.html',
    styleUrls: [
      '../../../../assets/css/infrastructure.css'
    ]
  })
export class ZoneAssignedAssetsComponent implements OnInit {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent)
  indefiniteProgressDialog!: SimpleIndefiniteProgressDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent!: SimpleSuccessErrorDialogComponent;

  /** Dialog used to view / edit the assigned assets for a specific zone */
  @ViewChild(ZoneAssignedAssetsDialogComponent)
  zaaDialog!: ZoneAssignedAssetsDialogComponent;

  /** Holds the current page of data being displayed */
  zaas: Zaa_ZonesResponseDto[] = [];
  /** Holds the current page number being displayed (starts with 0) */
  currentPage: number = 1;
  /** Holds the total number of pages */
  total: number = 0;

  /** Holds the available options for the "Results Per Page" dropdown */
  resultsPerPageOptions: number[] = RESULTS_PER_PAGE;
  /** Holds the selected Page Size */
  selectedPageSize: number = this.resultsPerPageOptions[0];

  /** Holds the selected Zaas */
  selectedZaas: Map<number, Zaa_ZonesResponseDto> = new Map();

  /** Represents the "Remove Filter" option for the available Filters */
  FilterOption_RemoveFilter: FilterOption = { column: null, label: "REMOVE FILTER" } as FilterOption;
  /** Holds the available Filter Options */
  filterOptions: FilterOption[] = [ this.FilterOption_RemoveFilter, { "column": "zone_name", "label": "Zone" } as FilterOption ];
  /** Holds the selected Filter Option (Or a reference to the "RemoveFilter" option if none are selected) */
  selectedFilterOption: FilterOption = this.FilterOption_RemoveFilter;
  /** Current Filter Value for the selected Filter Option */
  filterValue: string = "";


  /** Constructor */
  constructor(
    private route: ActivatedRoute,
    private zaaService: ZoneAssignedAssetsService
  ) { }

  /**
   * Angular Lifecycle hook.  Called after the component is created, but 
   * before the views are created.
   * Grabs the data that was obtained while the page was loading and 
   * updates the current displayed data.
   */
  ngOnInit(): void {
    this.route.data.subscribe(data => {
      const zaaResponses = (data['zaaResponses'] as PagedResults<Zaa_ZonesResponseDto>);
      this.setZaaResponses(zaaResponses);
    });
  }

  /**
   * Sets the displayed list of items for the current page. 
   * This also updates the total number of items based on the PagedResults
   * passed in.
   */
  private setZaaResponses(zaas: PagedResults<Zaa_ZonesResponseDto>): void {
    // Clear the current list
    while (this.zaas.length > 0) {
      this.zaas.pop();
    }

    // Add the new ones to the list and update the total
    this.zaas.push(...zaas.data);
    this.total = zaas.size;
    console.log(`Setting initial Zaas: ${this.zaas}, Total #: ${this.total}`);
  }

  /**
   * Refreshes the current data being displayed.  This takes into account 
   * the current page number and any filtering that the User has applied. 
   */
  async refreshData(): Promise<void> {
    this.indefiniteProgressDialog.show("Refreshing", "Refreshing Data...");

    let caughtError: any = null;
    try {
      // Query for the new data
      let start = (this.currentPage - 1) * this.selectedPageSize;
      let filterColumn: string | null;
      let filterValue: string | null;
      if (this.selectedFilterOption == this.FilterOption_RemoveFilter) {
        filterColumn = null;
        filterValue = null;
      } else {
        filterColumn = this.selectedFilterOption.column;
        filterValue = this.filterValue;
      }
      let pagedData: PagedResults<Zaa_ZonesResponseDto> = await this.zaaService.getZonesWithAssetCount(start, this.selectedPageSize, filterColumn, filterValue);

      // Set the data to the display
      this.setZaaResponses(pagedData);
    } catch(error) {
      caughtError = error;
    } finally {
      this.indefiniteProgressDialog.hide();
    }

    if (caughtError != null) {
      this.handleError(caughtError);
    }
  }

  /**
   * Returns a boolean indicating if a single item is selected.
   */
  isSingleItemSelected(): boolean {
    return this.selectedZaas.size == 1;
  }

  /**
   * Returns a boolean indicating if all items on the current page 
   * are selected.
   */
  isAllSelected(): boolean {
    return this.selectedZaas.size == this.zaas.length;
  }

  /**
   * Called when the User toggles the "Select All" checkbox in the UI.
   * Either adds or removes all current Zaas on the page from being selected
   * depending on the event. 
   */
  selectAllToggled(event: any): void {
    if (event?.target?.checked) {
      for (let zaa of this.zaas) {
        if (zaa?.zone?.id) {
          this.selectedZaas.set(zaa.zone.id, zaa);
        }
      } 
    } else {
      for (let zaa of this.zaas) {
        if (zaa?.zone?.id) {
          this.selectedZaas.delete(zaa.zone.id);
        }
      } 
    }
  }

  /**
   * Called when the User toggles a Zaa in the UI.  
   * Adds or removes the given Zaa from being selected depending on the event. 
   */
  selectItemToggled(zaa: Zaa_ZonesResponseDto, event: any): void {
    if (event?.target?.checked) {
        if (zaa?.zone?.id) {
          this.selectedZaas.set(zaa.zone.id, zaa);
        }
    } else {
        if (zaa?.zone?.id) {
          this.selectedZaas.delete(zaa.zone.id);
        }
    }
  }

  /**
   * Returns a boolean indicating if the given Zone ID is selected.
   */
  isSelected(zoneId: number | null | undefined): boolean {
    return zoneId == null || zoneId == undefined? false : this.selectedZaas.get(zoneId) != null;
  }

  /**
   * Called when a single item is selected and the User clicks the "Edit" Action
   * in the UI.  Displays the selected item for editing.
   */
  editItemClicked(): void {
    if (this.isSingleItemSelected()) {
      this.editAssignedAssetsForZone(Array.from(this.selectedZaas.values())[0]);
    }
  }

  /**
   * Called when an Item is clicked in the UI Table.
   * Displays the Item for editing.
   */
  itemClicked(zaa: Zaa_ZonesResponseDto): void {
    this.editAssignedAssetsForZone(zaa);
  }

  /**
   * Displays the Assigned Assets Dialog for the given Zone
   */
  private editAssignedAssetsForZone(zaa: Zaa_ZonesResponseDto): void {
    this.indefiniteProgressDialog.show("Loading", `Obtaining Assets assigned to Zone: ${zaa?.zone?.name}...`)
    this._editAssignedAssetsForZone(zaa);
  }

  /**
   * Loads the Assets assigned to the Zone and displays the Dialog
   */
  private async _editAssignedAssetsForZone(zaa: Zaa_ZonesResponseDto): Promise<void> {
    let assignedAssets: Zaa_AssetDto[] = [];
    let error: any = null;
    try {
      if (zaa.zone?.id != null) {
        assignedAssets = await this.zaaService.getAssetsForZoneId(zaa.zone.id);
      }
    } catch (e) {
      error = e;
    } finally {
      this.indefiniteProgressDialog.hide();
    }

    if (error != null) {
      this.handleError(error);
    } else {
      this.zaaDialog.show(zaa, assignedAssets);
    }
  }

  /**
   * Called when the dialog indicates that changes have been made 
   * and we need to refresh the data
   */
  onRefreshParent(): void {
      this.refreshData();
  }

  /**
   * Called when the User selects a Filter Option.
   * If changing, this sets the selected Filter Option, clears the Filter Value,
   * sets the current page to 1 and refreshes the data. 
   */
  setFilterOption(filterOption: FilterOption): void {
    if (this.selectedFilterOption != filterOption) {
      this.selectedFilterOption = filterOption;
      this.selectedZaas.clear();
      this.filterValue = "";
      this.currentPage = 1;
      this.refreshData();
    }
  }

  /**
   * Called when the User changes the value for the selected Filter Option.
   * Sets the current page back to 1 and refreshes the data.
   */
  filterValueChanged(): void {
    this.selectedZaas.clear();
    this.currentPage = 1;
    this.refreshData();
  }

  /**
   * Called when the User selects a new page size.
   * If the page size is changing, this updates it and refreshes the data.
   */
  setPageSize(pageSize: number): void {
    if (this.selectedPageSize != pageSize) {
      this.selectedZaas.clear();
      this.selectedPageSize = pageSize;
      this.currentPage = 1;
      this.refreshData();
    }
  }

  /**
   * Called when the pagination changes the page. 
   * Updates the current page number and refreshes the data.
   */
  pageChange(pageNumber: number): void {
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }

    if (this.currentPage == pageNumber) {
      return;
    }

    this.selectedZaas.clear();
    this.currentPage = pageNumber;
    this.refreshData();
  }

  /**
   * Displays the error to the User
   */
  handleError(error: any): void {
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    } 
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}

/**
 * Represents an option for filtering the results
 */
interface FilterOption {
  column : string | null;
  label : string | null;
}